import { Badge, Button, Switch, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatDate, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { type PayoutPayment, PayoutPaymentModal } from './PayoutPaymentModal';

/**
 * Paying the drivers, which is what starting a payout was always for.
 *
 * The office could gather a month's commission into a payout and then had nowhere to see it: the
 * list existed only in the reply to the button that made it. Nothing could be marked as handed
 * over, so commission kept accruing against partners who had already been paid in cash, and the
 * "what we owe" figures grew for ever. Marking a payout paid is deliberately kept away from the
 * assistant, which leaves this screen as the only place it can be done at all.
 */

const LINES_PATH = '/admin/v1/referrals/payout-lines';
const RUNS_PATH = '/admin/v1/referrals/payout-runs';

interface PayoutRun {
  id?: string;
  periodStart?: string;
  periodEnd?: string;
}

/** What is still owed on a line after any part-payment. */
function remainingMinor(row: Record<string, unknown>): number {
  const amount = typeof row.amountMinor === 'number' ? row.amountMinor : 0;
  const paid = typeof row.paidMinor === 'number' ? row.paidMinor : 0;
  return Math.max(amount - paid, 0);
}

function StatusCell({ row }: { row: Record<string, unknown> }) {
  const currency = (row.currency as string) ?? 'HNL';
  switch (row.status) {
    case 'paid':
      return (
        <Badge color="teal" variant="light">
          Paid
        </Badge>
      );
    case 'partiallyPaid':
      return (
        <Text size="xs" c="orange">
          {formatMoney(remainingMinor(row), currency)} still to hand over
        </Text>
      );
    case 'belowThreshold':
      // Not a blocked payment — cash can always be handed over. The minimum that stopped it is
      // whichever one was set for the way this partner asked to be paid, so the wording does not
      // name the transfer: a partner paid in cash can have a cash minimum, and telling him it was
      // the transfer one sends the office to hand over money it had decided to hold.
      return (
        <Text size="xs" c="dimmed">
          Hasn’t reached the minimum set for them — cash can still be handed over
        </Text>
      );
    default:
      return (
        <Badge color="yellow" variant="light">
          Ready to pay
        </Badge>
      );
  }
}

function PayButton({ row, refresh }: { row: Record<string, unknown>; refresh: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const remaining = remainingMinor(row);
  const currency = (row.currency as string) ?? 'HNL';

  const submit = async (payment: PayoutPayment) => {
    setSaving(true);
    try {
      await rawApi('POST', `${LINES_PATH}/${String(row.id)}/pay`, payment);
      notifyDone(
        'Payment recorded',
        payment.amountMinor >= remaining
          ? 'Their commission is settled up to this payout.'
          : `${formatMoney(remaining - payment.amountMinor, currency)} of this payout is still outstanding.`,
      );
      setOpen(false);
      refresh();
    } catch (err) {
      notifyError(err, 'record this payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpen(true)}>
        Record payment
      </Button>
      {/* Mounted only while it is open, so the amount box opens on what is outstanding NOW rather
          than on whatever it was the first time this row was rendered. */}
      {open && (
        <PayoutPaymentModal
          opened
          onClose={() => setOpen(false)}
          title="Record a payment"
          outstandingMinor={remaining}
          currency={currency}
          belowThreshold={row.status === 'belowThreshold'}
          submitting={saving}
          onSubmit={(payment) => void submit(payment)}
        />
      )}
    </>
  );
}

export function PayoutsSection() {
  const queryClient = useQueryClient();
  const [includePaid, setIncludePaid] = useState(false);
  // Sent only when it is on. The API coerces this flag from the query string, where the string
  // "false" is every bit as true as "true" — so asking for the short list by name would return the
  // long one, and a payout already handed over would look like one still waiting.
  const listQuery = includePaid ? { includePaid: 'true' } : {};
  const runs = useQuery({
    queryKey: listKey(RUNS_PATH),
    queryFn: () => rawApi<PayoutRun[]>('GET', RUNS_PATH),
  });
  const periods = new Map((runs.data ?? []).map((run) => [run.id ?? '', run]));
  const partners = useEntityIndex('referralProfile');

  /*
   * Nothing here hides a row any more, and that is a repair rather than a loss.
   *
   * A payout run used to gather commission without claiming it, so starting a second one wrote a
   * second payable row for money that is owed once. This screen — and the partner's own page —
   * carried a rule that worked out which of them was stale and greyed it out, while the API would
   * happily pay either. The run now takes the earlier untouched line over, so the duplicate does
   * not exist to hide: every row on this list is money somebody is still owed.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(LINES_PATH) });
    // Paying somebody closes their accruals, so what we owe them changes at the same moment.
    void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/referrals/balances') });
  };

  return (
    <CrudSection
      title="Payouts"
      description="Each partner's share from a payout you started, and what has actually been handed over. Recording a payment here is what clears their commission — until then it keeps building up."
      listPath={LINES_PATH}
      listQuery={listQuery}
      emptyMessage={
        includePaid
          ? 'No payouts yet. Start one from "What we owe" when it is time to pay everybody.'
          : 'Nobody is waiting to be paid. Everything from your payouts has been handed over.'
      }
      actions={
        <Switch
          size="xs"
          label="Include ones already paid"
          checked={includePaid}
          onChange={(e) => setIncludePaid(e.currentTarget.checked)}
        />
      }
      columns={[
        {
          accessor: 'profileId',
          title: 'Partner',
          render: (r) => partners.label(r.profileId),
        },
        {
          accessor: 'payoutRunId',
          title: 'Covers',
          noWrap: true,
          render: (r) => {
            const run = periods.get(String(r.payoutRunId ?? ''));
            return run ? `${formatDate(run.periodStart)} – ${formatDate(run.periodEnd)}` : '—';
          },
        },
        { accessor: 'amountMinor', title: 'Commission', unit: 'money', currencyAccessor: 'currency' },
        {
          accessor: 'status',
          title: 'Where it stands',
          render: (r) => <StatusCell row={r} />,
        },
        { accessor: 'methodUsed', title: 'Paid by', enumGroup: 'payoutMethod' },
        { accessor: 'reference', title: 'Reference' },
        { accessor: 'paidAt', title: 'Paid on', unit: 'datetime', noWrap: true },
      ]}
      rowActions={(row) => (row.status === 'paid' ? null : <PayButton row={row} refresh={refresh} />)}
    />
  );
}
